import { useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  Card,
  CardHeader,
  ContextLine,
  CurrencyAmountUI,
  DateUI,
  DirectionIcon,
  FXTradeCard,
  Label,
  Number,
  PrimaryFullScreenSpinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TransactionStatusUI,
  Txt,
  transactionTypeToBadgeLabels,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { FC, ReactNode } from "react";
import { Suspense } from "react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { PiBuildingOffice } from "react-icons/pi";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  AdminTransactionDetailFragment,
  AssetCardFieldsFragment,
  BeneficiaryAssetCardFieldsFragment,
  FxTradeCardFieldsFragment,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/transactions_/$transactionId")({
  head: () => opsHead("Transaction"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { transactionId } = Route.useParams();
  const txnId = transactionId;

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminTransactionIdPage($id: ID!) {
        node(id: $id) {
          ... on Transaction {
            ...AdminTransactionDetail
          }
        }
      }

      fragment AdminTransactionDetail on Transaction {
        id
        createdAt
        type
        direction
        description
        shortRef
        reference
        currencyAmount {
          currency
          amount
        }
        effectiveAt
        postedAt
        postedDate
        status
        unitAmount {
          unit
          amount
        }
        ledgerAccount {
          ... on AssetLedgerAccount {
            asset {
              ...AssetCardFields
              businessEntity {
                id
                name
                organisation {
                  id
                  name
                  squareLogoUrl
                }
              }
            }
          }
        }
        fundMovement {
          ... on Deposit {
            id
            destinationAsset {
              ...AssetCardFields
              businessEntity {
                id
                name
                organisation {
                  id
                  name
                  squareLogoUrl
                }
              }
            }
          }
          ... on Transfer {
            id
            sourceAsset {
              ...AssetCardFields
            }
            destinationAccount {
              ...AssetCardFields
              ...BeneficiaryAssetCardFields
            }
          }
          ... on EburyFXTrade {
            id
            ...FXTradeCardFields
          }
          ... on FundUnitOrder {
            id
          }
        }
        tags {
          id
          key
          value
        }
        fundMovementOptions {
          ... on FundMovementTransactionTransferOption {
            id
            matchedWithReference
            transfer {
              id
            }
          }
          ... on FundMovementTransactionTransferNewOption {
            id
            transferId
            asset {
              id
              name
            }
          }
          ... on FundMovementTransactionNewDepositOption {
            id
          }
          ... on FundMovementTransactionAnnotationOption {
            id
            annotation
          }
          ... on FundMovementTransactionFundUnitOrderOption {
            id
            fundUnitOrder {
              id
            }
          }
          ... on FundMovementTransactionWestpacPaymentInstructionOption {
            id
            westpacPaymentInstruction {
              id
            }
          }
          ... on FundMovementTransactionStripePayoutOption {
            id
            stripePayout {
              id
            }
          }
        }
      }
    `),
    {
      variables: {
        id: txnId,
      },
    },
  );

  const txn = data.node;
  if (!txn || txn.__typename !== "Transaction") {
    throw new NotFoundError();
  }

  const {
    id,
    currencyAmount,
    unitAmount,
    direction,
    reference,
    shortRef,
    status,
    description,
  } = txn;

  useOpsTitle(`Transaction #${shortRef}`);

  const fundMovementOptions = txn.fundMovementOptions ?? [];
  const tags = txn.tags ?? [];

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={
          <div className="flex flex-row justify-between gap-2">
            {unitAmount && (
              <TooltipProvider delayDuration={50}>
                <Tooltip>
                  <TooltipTrigger>
                    <HiOutlineInformationCircle />
                  </TooltipTrigger>
                  <TooltipContent>
                    {unitAmount && (
                      <Number
                        value={unitAmount.amount.mul(
                          direction === "DEBIT" ? -1 : 1,
                        )}
                        format={Number.format.units(
                          unitAmount.amount.mul(direction === "DEBIT" ? -1 : 1),
                        )}
                      />
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            <div className="w-full text-right">
              <span className="flex flex-row justify-end gap-1">
                <CurrencyAmountUI
                  amount={currencyAmount.amount.mul(
                    direction === "DEBIT" ? -1 : 1,
                  )}
                  currency={currencyAmount.currency}
                />
              </span>
            </div>
          </div>
        }
        contextLine={
          <ContextLine
            current={`#${shortRef}`}
            ancestors={[{ label: "Transaction", path: "/transactions" }]}
          />
        }
      />
      <div className="flex flex-col gap-12">
        <Card variant="panel">
          <CardHeader>Transaction details</CardHeader>

          <div className="flex w-full flex-col gap-0">
            <DetailRow
              label="Status"
              value={<TransactionStatusUI status={status} key="status" />}
            />
            {!unitAmount ? (
              <DetailRow
                label="Amount"
                value={
                  <div className="flex flex-row gap-1">
                    <CurrencyAmountUI
                      amount={currencyAmount.amount}
                      currency={currencyAmount.currency}
                    />
                    <DirectionIcon direction={direction} />
                  </div>
                }
              />
            ) : (
              <DetailRow
                label="Units"
                value={
                  <Number
                    value={unitAmount.amount}
                    format={Number.format.units(unitAmount.amount)}
                  />
                }
              />
            )}

            <DetailRow
              label="Effective at"
              value={
                <Txt>
                  <DateUI date={txn.effectiveAt} variant="DATETIME_TZ" />
                </Txt>
              }
            />
            <DetailRow
              label="Posted at"
              value={
                <Txt>
                  {txn.postedAt ? (
                    <DateUI date={txn.postedAt} variant="DATETIME_TZ" />
                  ) : (
                    "-"
                  )}
                </Txt>
              }
            />
            <DetailRow
              label="Posted date"
              value={<Txt>{txn.postedDate ?? "-"}</Txt>}
            />
            <DetailRow
              label="Created at"
              value={
                <Txt>
                  <DateUI date={txn.createdAt} variant="DATETIME_TZ" />
                </Txt>
              }
            />
            <DetailRow label="Asset" value={getAssetFlow(txn)} />

            <DetailRow
              label="Transaction number"
              value={<span className="font-mono">{`#${shortRef}`}</span>}
            />
            <DetailRow label="Description" value={description} />
            <DetailRow label="Reference" value={reference} />
            <DetailRow
              label="Business Entity"
              value={getBusinessEntityCard(txn)}
            />
            <DetailRow
              label="Tags"
              value={
                tags.length === 0 ? (
                  <Txt>-</Txt>
                ) : (
                  <div className="flex flex-col gap-2">
                    {tags.map((tag) => (
                      <div key={tag.id} className="flex flex-col gap-0.5">
                        <Txt variant="name">{tag.key}</Txt>
                        {tag.value && (
                          <Txt variant="description">{tag.value}</Txt>
                        )}
                      </div>
                    ))}
                  </div>
                )
              }
            />
          </div>
          {
            <div className="flex flex-row gap-2">
              <ViewParentButton fundMovement={txn.fundMovement ?? undefined} />

              <Link
                to={`/ledger/transactions/${id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Button className="w-fit" variant="outline">
                  View ledger transaction
                </Button>
              </Link>
            </div>
          }
        </Card>

        {fundMovementOptions && fundMovementOptions.length > 0 && (
          <Card variant="panel">
            <CardHeader>Fund movement options</CardHeader>
            <div className="flex flex-col gap-2">
              {fundMovementOptions.map((opt) => (
                <FundMovementOptionRow key={opt.id} option={opt} />
              ))}
            </div>
          </Card>
        )}
      </div>
    </AdminPageContainer>
  );
}

const DetailRow: FC<{
  label: ReactNode;
  value: ReactNode;
}> = ({ label, value }) =>
  value ? (
    <div className="flex w-full border-grid border-b py-4">
      <div className="w-40">
        <Label>{label}</Label>
      </div>
      <div className="text-sm leading-6">{value}</div>
    </div>
  ) : null;

function ViewParentButton({
  fundMovement,
}: {
  fundMovement:
    | {
        id: string;
        __typename: "FundUnitOrder" | "Deposit" | "EburyFXTrade" | "Transfer";
      }
    | undefined;
}) {
  if (!fundMovement) {
    return null;
  }

  switch (fundMovement.__typename) {
    case "Transfer":
      return (
        <Link
          to={`/transfers/${fundMovement.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Button className="w-fit" variant="outline">
            View transfer
          </Button>
        </Link>
      );
    case "Deposit":
      return (
        <Link
          to={`/deposits/${fundMovement.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Button className="w-fit" variant="outline">
            View deposit
          </Button>
        </Link>
      );
    case "EburyFXTrade":
      return (
        <Link
          to={`/fx-trades/${fundMovement.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Button className="w-fit" variant="outline">
            View FX Trade
          </Button>
        </Link>
      );
    case "FundUnitOrder":
      return (
        <Link
          to={`/fund-unit-orders/${fundMovement.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Button className="w-fit" variant="outline">
            View fund unit order
          </Button>
        </Link>
      );
    default:
      return null;
  }
}

const getAssetFlow = (txn: AdminTransactionDetailFragment) => {
  const type = txn.type;
  const [FirstBadge, SecondBadge] = type
    ? transactionTypeToBadgeLabels[type]
    : [];

  let firstAccount:
    | AssetCardFieldsFragment
    | BeneficiaryAssetCardFieldsFragment
    | undefined
    | null = undefined;
  let secondAccount:
    | AssetCardFieldsFragment
    | BeneficiaryAssetCardFieldsFragment
    | undefined
    | null = undefined;
  let fxTrade: FxTradeCardFieldsFragment | undefined | null = undefined;

  const assetUrlBase = `/assets`;
  const beneUrlBase = `/beneficiary-accounts`;

  switch (txn.fundMovement?.__typename) {
    case "Deposit": {
      firstAccount = txn.fundMovement.destinationAsset;
      break;
    }
    case "Transfer": {
      firstAccount =
        txn.type === "TRANSFER_IN"
          ? txn.fundMovement.destinationAccount
          : txn.fundMovement.sourceAsset;
      secondAccount =
        txn.type === "TRANSFER_IN"
          ? txn.fundMovement.sourceAsset
          : txn.fundMovement.destinationAccount;
      break;
    }
    case "EburyFXTrade":
      if (
        txn.ledgerAccount?.__typename === "AssetLedgerAccount" &&
        txn.ledgerAccount.asset
      ) {
        firstAccount = txn.ledgerAccount.asset;
        fxTrade = txn.fundMovement;
      }
      break;
    case "FundUnitOrder":
      firstAccount =
        txn.ledgerAccount?.__typename === "AssetLedgerAccount"
          ? txn.ledgerAccount.asset
          : undefined;
      break;
    case undefined: {
      firstAccount =
        txn.ledgerAccount?.__typename === "AssetLedgerAccount"
          ? txn.ledgerAccount.asset
          : undefined;
    }
  }

  return (
    <div className="flex flex-row gap-2">
      {(FirstBadge || firstAccount) && (
        <div className="flex min-w-0 flex-1 basis-0 flex-col">
          {FirstBadge && FirstBadge}
          {firstAccount && (
            <AccountCard
              account={firstAccount}
              variant="COMPACT_CARD"
              linkTo={
                firstAccount.__typename === "BeneficiaryAccount"
                  ? `${beneUrlBase}/${firstAccount.id}`
                  : `${assetUrlBase}/${firstAccount.id}`
              }
            />
          )}
        </div>
      )}
      {(SecondBadge || secondAccount) && (
        <div className="flex flex-1 basis-0 flex-col">
          {SecondBadge && SecondBadge}
          {secondAccount && (
            <AccountCard
              account={secondAccount}
              variant="COMPACT_CARD"
              linkTo={
                secondAccount.__typename === "BeneficiaryAccount"
                  ? `${beneUrlBase}/${secondAccount.id}`
                  : `${assetUrlBase}/${secondAccount.id}`
              }
            />
          )}
          {fxTrade && <FXTradeCard fxTrade={fxTrade} />}
        </div>
      )}
    </div>
  );
};

function FundMovementOptionRow({
  option,
}: {
  option: NonNullable<
    AdminTransactionDetailFragment["fundMovementOptions"]
  >[number];
}) {
  let label: string;
  let detail: string;

  switch (option.__typename) {
    case "FundMovementTransactionTransferOption":
      label = "Transfer";
      detail = option.transfer?.id ?? option.id;
      break;
    case "FundMovementTransactionTransferNewOption":
      label = "New Transfer";
      detail = `${option.transferId}${option.asset ? ` → ${option.asset.name}` : ""}`;
      break;
    case "FundMovementTransactionNewDepositOption":
      label = "New Deposit";
      detail = option.id;
      break;
    case "FundMovementTransactionAnnotationOption":
      label = "Annotation";
      detail = option.annotation;
      break;
    case "FundMovementTransactionFundUnitOrderOption":
      label = "Fund Unit Order";
      detail = option.fundUnitOrder.id;
      break;
    case "FundMovementTransactionWestpacPaymentInstructionOption":
      label = "Westpac Payment Instruction";
      detail = option.westpacPaymentInstruction.id;
      break;
    case "FundMovementTransactionStripePayoutOption":
      label = "Stripe Payout";
      detail = option.stripePayout.id;
      break;
    default:
      return null;
  }

  return (
    <div className="flex flex-row items-center gap-4 rounded border px-4 py-3 text-sm">
      <span className="w-56 shrink-0">
        <Txt>{label}</Txt>
      </span>
      <Txt>{detail}</Txt>
    </div>
  );
}

const getBusinessEntityCard = (txn: AdminTransactionDetailFragment) => {
  let beName: string | undefined = undefined;
  let orgName: string | undefined = undefined;
  let logoUrl: string | undefined = undefined;
  let url: string = ".";

  if (
    txn.ledgerAccount?.__typename === "AssetLedgerAccount" &&
    txn.ledgerAccount.asset &&
    txn.ledgerAccount.asset?.businessEntity
  ) {
    const be = txn.ledgerAccount.asset?.businessEntity;
    beName = be.name;
    orgName = be.organisation?.name ?? "";
    logoUrl = be.organisation?.squareLogoUrl ?? "";
    url = `/organisations/${be.organisation?.id}/business-entities/${be.id}`;
  }

  return (
    <Link
      to={url}
      className="flex min-w-0 flex-row items-center gap-1 rounded-sm border p-2 shadow-surface hover:border"
    >
      {logoUrl ? (
        <img
          className="mr-2 size-8 shrink-0 rounded-sm"
          src={logoUrl}
          alt="Business entity logo"
        />
      ) : (
        <PiBuildingOffice className="mr-2 shrink-0 text-sm text-tertiary" />
      )}
      <div className="flex min-w-0 flex-col">
        <div className="truncate">
          <Txt variant="body" truncate>
            {beName && beName}
          </Txt>
        </div>
        <div className="truncate">
          <Txt variant="description" truncate>
            {orgName && orgName}
          </Txt>
        </div>
      </div>
    </Link>
  );
};
